'use client';

import { useState } from 'react';
import { Box, Typography, Button, Stack } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { uploadToBlob } from 'lib/blob';
import { Project, ProjectsGalleryProps } from './types';
import ProjectCard from './ProjectCard';
import NewProjectDialog from './NewProjectDialog';
import DeleteProjectDialog from './DeleteProjectDialog';
import ErrorDialog from './ErrorDialog';
import AssetGalleryModal from '../AssetGalleryModal';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

export default function ProjectsGallery({
  initialProjects,
  userId,
  isOwnProfile,
}: ProjectsGalleryProps) {
  const [projects, setProjects] = useState(initialProjects);
  const [visibleProjects, setVisibleProjects] = useState(3);
  const [isNewProjectDialogOpen, setIsNewProjectDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [selectedAsset, setSelectedAsset] = useState<{ projectId: string; index: number } | null>(
    null
  );

  const handleCreateProject = async (title: string, description: string) => {
    try {
      const response = await fetch('/api/projects', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title, description }),
        credentials: 'include',
      });

      if (!response.ok) throw new Error('Failed to create project');

      const newProject = await response.json();
      setProjects(prev => [newProject, ...prev]);
      setIsNewProjectDialogOpen(false);
    } catch (error) {
      console.error('Error creating project:', error);
      setErrorMessage('Failed to create project. Please try again.');
    }
  };

  const handleUploadFiles = async (projectId: string, files: File[]) => {
    try {
      const uploadPromises = files.map(async file => {
        const blobUrl = await uploadToBlob(file);
        const isSTL =
          file.type === 'model/stl' ||
          file.type === 'application/octet-stream' ||
          file.name.toLowerCase().endsWith('.stl');
        return {
          url: blobUrl,
          name: file.name,
          size: file.size,
          type: isSTL ? 'STL' : 'image',
        };
      });

      const uploadedFiles = await Promise.all(uploadPromises);

      const response = await fetch(`/api/projects/${projectId}/files`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ files: uploadedFiles }),
        credentials: 'include',
      });

      if (!response.ok) throw new Error('Failed to save files');

      const updatedProject = await response.json();
      setProjects(prev => prev.map(p => (p.id === projectId ? updatedProject : p)));
    } catch (error) {
      console.error('Error uploading files:', error);
      setErrorMessage('Failed to upload files. Please try again.');
    }
  };

  const handleDeleteProject = async () => {
    if (!projectToDelete) return;

    try {
      const response = await fetch(`/api/projects/${projectToDelete}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) throw new Error('Failed to delete project');

      setProjects(prev => prev.filter(p => p.id !== projectToDelete));
      setProjectToDelete(null);
      setIsDeleteDialogOpen(false);
    } catch (error) {
      console.error('Error deleting project:', error);
      setErrorMessage('Failed to delete project. Please try again.');
    }
  };

  const handleDeleteFile = async (projectId: string, fileId: string) => {
    try {
      const response = await fetch(`/api/projects/${projectId}/files/${fileId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) throw new Error('Failed to delete file');

      const updatedProject = await response.json();
      setProjects(prev => prev.map(p => (p.id === projectId ? updatedProject : p)));
    } catch (error) {
      console.error('Error deleting file:', error);
      setErrorMessage('Failed to delete file. Please try again.');
    }
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <Stack spacing={3}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" component="h2">
            Projects
          </Typography>
          {isOwnProfile && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setIsNewProjectDialogOpen(true)}
            >
              New Project
            </Button>
          )}
        </Stack>

        <Stack spacing={2}>
          {projects.slice(0, visibleProjects).map(project => (
            <ProjectCard
              key={project.id}
              project={project}
              isOwnProfile={isOwnProfile}
              onUpload={files => handleUploadFiles(project.id, files)}
              onDelete={() => {
                setProjectToDelete(project.id);
                setIsDeleteDialogOpen(true);
              }}
              onSelectAsset={index => setSelectedAsset({ projectId: project.id, index })}
              onDeleteFile={handleDeleteFile}
            />
          ))}
        </Stack>

        {visibleProjects < projects.length && (
          <Box display="flex" justifyContent="center">
            <Button onClick={() => setVisibleProjects(prev => prev + 3)}>Show More</Button>
          </Box>
        )}

        <NewProjectDialog
          isOpen={isNewProjectDialogOpen}
          onClose={() => setIsNewProjectDialogOpen(false)}
          onSubmit={handleCreateProject}
        />

        <DeleteProjectDialog
          isOpen={isDeleteDialogOpen}
          onClose={() => {
            setProjectToDelete(null);
            setIsDeleteDialogOpen(false);
          }}
          onConfirm={handleDeleteProject}
        />

        <ErrorDialog message={errorMessage} onClose={() => setErrorMessage(null)} />

        {selectedAsset && (
          <AssetGalleryModal
            open={!!selectedAsset}
            assets={projects.find(p => p.id === selectedAsset.projectId)?.files || []}
            initialAssetIndex={selectedAsset.index}
            isOwnProfile={isOwnProfile}
            onClose={() => setSelectedAsset(null)}
            onRename={async (assetId: string, newName: string) => {
              try {
                const response = await fetch(
                  `/api/projects/${selectedAsset.projectId}/files/${assetId}/rename`,
                  {
                    method: 'PATCH',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ name: newName }),
                    credentials: 'include',
                  }
                );

                if (!response.ok) throw new Error('Failed to rename file');

                const updatedProject = await response.json();
                setProjects(prev =>
                  prev.map(p => (p.id === selectedAsset.projectId ? updatedProject : p))
                );
              } catch (error) {
                console.error('Error renaming file:', error);
                setErrorMessage('Failed to rename file. Please try again.');
              }
            }}
          />
        )}
      </Stack>
    </DndProvider>
  );
}
